/**
 * Stego decoder — extracts blob, derives key, decrypts, verifies integrity.
 *
 * Auto-detects technique (metadata vs polyglot) based on the carrier MP3.
 * Blob layout: salt(16) + header(77) + ciphertext(N)
 */

import { deriveKey, SALT_LENGTH } from "../crypto/argon2.js";
import { decryptPayload } from "../crypto/aes-gcm.js";
import { sha256, toHex } from "../crypto/utils.js";
import {
  HEADER_SIZE,
  unpackHeader,
  Technique,
  type StegoHeader,
} from "./header.js";
import { extractFromCoverArt } from "./techniques/cover-art.js";
import { extractLsbPayload } from "./techniques/lsb.js";
import { extractMetadataPayload, extractApicImage } from "./techniques/metadata.js";
import { extractPolyglot } from "./techniques/polyglot.js";

export interface DecodeResult {
  plaintext: Uint8Array;
  header: StegoHeader;
  technique: Technique;
}

/**
 * Decode a steganographic MP3 file.
 * Auto-detects technique by trying metadata first, then polyglot.
 */
export async function decodeCarrier(
  mp3Data: Uint8Array,
  passphrase: string
): Promise<DecodeResult> {
  // Try metadata technique first (ID3 PRIV frame)
  let rawBlob = extractMetadataPayload(mp3Data);
  let detectedTechnique = Technique.METADATA;

  // If no metadata payload, try APIC frame (cover art LSB)
  if (!rawBlob) {
    const apicImage = extractApicImage(mp3Data);
    if (apicImage) {
      rawBlob = await extractFromCoverArt(apicImage);
      detectedTechnique = Technique.COVER_ART;
    }
  }

  // If no APIC payload, try LSB audio
  if (!rawBlob) {
    rawBlob = extractLsbPayload(mp3Data);
    if (rawBlob) detectedTechnique = Technique.LSB;
  }

  // If no LSB payload found, try polyglot
  if (!rawBlob) {
    rawBlob = await extractPolyglot(mp3Data);
    detectedTechnique = Technique.POLYGLOT;
  }

  if (!rawBlob) {
    throw new Error("No steganographic payload found in this file.");
  }

  return decodeBlob(rawBlob, passphrase, detectedTechnique);
}

/**
 * Decode all steganographic payloads from an MP3 file.
 * Tries every technique independently and returns all successes.
 */
export async function decodeCarrierAll(
  mp3Data: Uint8Array,
  passphrase: string
): Promise<DecodeResult[]> {
  const results: DecodeResult[] = [];

  // Try metadata technique (ID3 PRIV frame)
  try {
    const metadataBlob = extractMetadataPayload(mp3Data);
    if (metadataBlob) {
      const result = await decodeBlob(metadataBlob, passphrase, Technique.METADATA);
      results.push(result);
    }
  } catch {
    // Technique failed — skip
  }

  // Try cover art technique (APIC frame LSB)
  try {
    const apicImage = extractApicImage(mp3Data);
    if (apicImage) {
      const coverBlob = await extractFromCoverArt(apicImage);
      if (coverBlob) {
        const result = await decodeBlob(coverBlob, passphrase, Technique.COVER_ART);
        results.push(result);
      }
    }
  } catch {
    // Technique failed — skip
  }

  // Try LSB audio technique (frame main_data LSBs)
  try {
    const lsbBlob = extractLsbPayload(mp3Data);
    if (lsbBlob) {
      const result = await decodeBlob(lsbBlob, passphrase, Technique.LSB);
      results.push(result);
    }
  } catch {
    // Technique failed — skip
  }

  // Try polyglot technique (appended ZIP)
  try {
    const polyglotBlob = await extractPolyglot(mp3Data);
    if (polyglotBlob) {
      const result = await decodeBlob(polyglotBlob, passphrase, Technique.POLYGLOT);
      results.push(result);
    }
  } catch {
    // Technique failed — skip
  }

  return results;
}

/**
 * Decode a raw stego blob (salt + header + ciphertext).
 * Used when you already have the extracted blob.
 */
export async function decodeBlob(
  rawBlob: Uint8Array,
  passphrase: string,
  technique?: Technique
): Promise<DecodeResult> {
  const minSize = SALT_LENGTH + HEADER_SIZE;
  if (rawBlob.length < minSize) {
    throw new Error(
      `Blob too small: ${rawBlob.length} bytes (minimum ${minSize}).`
    );
  }

  // Split blob
  const salt = rawBlob.slice(0, SALT_LENGTH);
  const headerData = rawBlob.slice(SALT_LENGTH, SALT_LENGTH + HEADER_SIZE);
  const ciphertext = rawBlob.slice(SALT_LENGTH + HEADER_SIZE);

  // Parse header
  const header = unpackHeader(headerData);
  if (!header) {
    throw new Error("Invalid stego header: magic bytes mismatch.");
  }

  const detectedTechnique = technique ?? header.technique;

  // Derive key with extracted salt
  const derived = await deriveKey(passphrase, salt);

  // Decrypt
  const plaintext = await decryptPayload(
    derived.key,
    ciphertext,
    header.nonce,
    header.authTag
  );

  // Integrity check (only for non-chunked payloads)
  if (!header.isChunked) {
    const hash = await sha256(plaintext);
    if (toHex(hash) !== toHex(header.payloadSha256)) {
      throw new Error(
        "Integrity check failed: SHA-256 mismatch (payload corrupted)."
      );
    }
  }

  return {
    plaintext,
    header,
    technique: detectedTechnique,
  };
}

/**
 * Decode a steganographic image file (PNG/JPEG with LSB-embedded payload).
 * Extracts blob from pixel LSBs, then decrypts.
 */
export async function decodeImage(
  imageData: Uint8Array,
  passphrase: string
): Promise<DecodeResult> {
  const rawBlob = await extractFromCoverArt(imageData);
  if (!rawBlob) {
    throw new Error("No steganographic payload found in this image.");
  }
  return decodeBlob(rawBlob, passphrase, Technique.COVER_ART);
}
